import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmailIcon from '@mui/icons-material/Email';
import ExtensionIcon from '@mui/icons-material/Extension';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { SEOHead, SEO_PAGES } from '../seo/SEOHead';

const faqs = [
  {
    question: 'How do I install the GovToolsPro extension?',
    answer: 'Visit the Chrome Web Store and search for "GovToolsPro" or click the "Add to Chrome" button on our homepage. Click "Add to Chrome" and the extension will be installed automatically. You\'ll see the GovToolsPro icon in your browser toolbar.'
  },
  {
    question: 'How do I use the extension on SAM.gov?',
    answer: 'Navigate to any contract opportunity page on SAM.gov. The extension will automatically detect when you\'re on a solicitation page. Click the GovToolsPro icon in your toolbar or open the sidepanel to see instant analysis, bid estimates, and competitor data.'
  },
  {
    question: 'What types of opportunities does GovToolsPro support?',
    answer: 'GovToolsPro works best with Solicitation-type opportunities on SAM.gov. This includes RFPs, RFQs, and other competitive procurement notices. Some features may have limited functionality on other notice types.'
  },
  {
    question: 'How accurate is the bid estimation feature?',
    answer: 'Bid estimates are based on historical government contract data from USAspending.gov and FPDS.gov. We analyze similar contracts by NAICS code, agency, and contract type to provide data-driven ranges. Estimates should be used as a starting point for your own pricing analysis - always conduct your own due diligence.'
  },
  {
    question: 'Is GovToolsPro affiliated with the government?',
    answer: 'No. GovToolsPro is an independent, privately-owned service. We are NOT affiliated with, endorsed by, or officially connected with any United States government agency, including SAM.gov, USAspending.gov, or FPDS.gov.'
  },
  {
    question: 'Is my data secure?',
    answer: 'Yes, we take security seriously. All data is encrypted in transit and at rest using Firebase\'s enterprise-grade security. We never share your personal data with third parties. See our Privacy Policy for complete details.'
  },
  {
    question: 'How much does GovToolsPro cost?',
    answer: 'GovToolsPro is currently free during our launch period. We plan to introduce a Pro tier ($19.99/month) with advanced features in the future. Existing free users will be notified before any changes to pricing.'
  },
  {
    question: 'Can I use GovToolsPro on browsers other than Chrome?',
    answer: 'Currently, GovToolsPro is only available as a Chrome extension. We may consider other browsers in the future based on user demand.'
  },
];

export const SupportPage: React.FC = () => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const mailtoLink = `mailto:support@govtoolspro.com?subject=${encodeURIComponent(contactForm.subject)}&body=${encodeURIComponent(`Name: ${contactForm.name}\nEmail: ${contactForm.email}\n\n${contactForm.message}`)}`;
    window.location.href = mailtoLink;
    setSubmitStatus('success');
    setContactForm({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <SEOHead {...SEO_PAGES.support} />
      <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, textAlign: 'center' }}>
        Support Center
      </Typography>
      <Typography variant="h6" color="text.secondary" sx={{ mb: 6, textAlign: 'center' }}>
        Get help with GovToolsPro - we're here to assist you
      </Typography>

      {/* Quick Links */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', textAlign: 'center' }}>
            <CardContent sx={{ py: 4 }}>
              <ExtensionIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>Get the Extension</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Install GovToolsPro from Chrome Web Store
              </Typography>
              <Button href="#chrome-store" variant="outlined" color="primary">
                Add to Chrome
              </Button>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', textAlign: 'center' }}>
            <CardContent sx={{ py: 4 }}>
              <EmailIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>Email Support</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Get help from our team
              </Typography>
              <Button href="mailto:support@govtoolspro.com" variant="outlined" color="primary">
                support@govtoolspro.com
              </Button>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', textAlign: 'center' }}>
            <CardContent sx={{ py: 4 }}>
              <HelpOutlineIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>FAQ</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Find answers to common questions
              </Typography>
              <Button href="#faq" variant="outlined" color="primary">
                View FAQ
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* FAQ Section */}
      <Paper id="faq" sx={{ p: 4, mb: 6 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          Frequently Asked Questions
        </Typography>
        {faqs.map((faq, index) => (
          <Accordion key={index} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 500 }}>{faq.question}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="text.secondary">{faq.answer}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>

      {/* Contact Form */}
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Contact Us
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Can't find what you're looking for? Send us a message and we'll get back to you.
        </Typography>

        {submitStatus === 'success' && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Your email client has been opened with your message. Send the email to complete your request.
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Your Name"
                name="name"
                value={contactForm.name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={contactForm.email}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                name="subject"
                value={contactForm.subject}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Message"
                name="message"
                multiline
                rows={4}
                value={contactForm.message}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Button type="submit" variant="contained" color="primary" size="large">
                Send Message
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Footer Info */}
      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Divider sx={{ mb: 4 }} />
        <Typography variant="body2" color="text.secondary">
          Email: support@govtoolspro.com
        </Typography>
      </Box>
    </Container>
  );
};
